/**
 * LocalAvatar - the local player's own pod body, visible only in third-person
 * view (src/entities/ChaseCameraRig.js).
 *
 * Built from the podAvatar.js kit ONLY - the exact rig remote players see -
 * and deliberately NOT registered in avatarManager.avatars: a mesh-backed
 * local record there would double-draw the minimap dot, make you a candidate
 * for your own video subscription, and burn a webcam-texture slot at distance
 * zero. The rig mirrors the player camera's position and yaw into the root
 * every frame (direct copy - no interpolation; the 10Hz LERP in AvatarManager
 * exists only to smooth network samples). With the root at the eye position,
 * the pod clone's -2 offset puts the model's feet on the floor, matching
 * exactly where remote clients render you from the position broadcast.
 *
 * Every mesh is isPickable = false via the kit, so crosshair picks pass
 * through it (the hotspot occlusion ray and the rig's boom raycast test
 * checkCollisions, which avatar meshes never set).
 */

import { TransformNode, Vector3, VideoTexture } from '@babylonjs/core';
import {
  loadPodTemplate,
  getStandbyTexture,
  createWebcamDisc,
  createDiscMaterial,
  POD_CLONE_OFFSET_Y,
} from './podAvatar.js';
import { UpdateManager } from '../utils/UpdateManager';
import { createErrorScope, ErrorCategory } from '../utils/sceneErrorHandler';

const errorHandler = createErrorScope('LocalAvatar', ErrorCategory.ENTITY);

export class LocalAvatar {
  /** @param {import('@babylonjs/core').Scene} scene */
  constructor(scene) {
    this.scene = scene;
    this._disposed = false;

    this.root = new TransformNode('localAvatar', scene);
    this.root.setEnabled(false);

    loadPodTemplate(scene)
      .then((templateRoot) => {
        if (this._disposed) return;
        const clone = templateRoot.clone('localAvatarPod', this.root);
        if (clone) {
          clone.setEnabled(true);
          clone.position = new Vector3(0, POD_CLONE_OFFSET_Y, 0);
        }
      })
      .catch((e) => errorHandler.warn('constructor', `pod template failed to load: ${e?.message}`));

    const { plane, ring, circleMask } = createWebcamDisc(scene, this.root, 'self');
    this._plane = plane;
    this._ring = ring;
    this._circleMask = circleMask;
    plane.material = createDiscMaterial(
      scene,
      'localAvatarDiscMat',
      getStandbyTexture(scene),
      circleMask,
    );

    // Live-face plumbing (mirrors arrivalPodPreview's setWebcamTrack recipe).
    /** @type {import('@babylonjs/core').VideoTexture|null} */
    this._webcamTexture = null;
    /** @type {HTMLVideoElement|null} */
    this._video = null;
    /** @type {string|null} */
    this._trackId = null;
    /** @type {typeof import('../videochat.js')|null} */
    this._videochat = null;
    this._videochatImport = null;
    this._pollId = UpdateManager.getInstance(scene).register(
      'LocalAvatarWebcam',
      () => this._pollWebcamTrack(),
      { throttle: 30 },
    );
  }

  /**
   * Keep the face disc in sync with the local camera track. videochat.js is
   * only ever dynamically imported (it carries the Agora SDK), so touch it
   * ONLY once its module-scope marker exists on window - never force the
   * chunk to load just to ask whether the camera is on.
   */
  _pollWebcamTrack() {
    if (this._disposed || !this.root.isEnabled()) return;
    if (!this._videochat) {
      if (typeof window === 'undefined' || !window.AGORA_VIDEO_PUBLISHERS) return;
      if (!this._videochatImport) {
        this._videochatImport = import('../videochat.js')
          .then((m) => {
            this._videochat = m;
          })
          .catch(() => {
            /* chunk unavailable - keep the standby face */
          });
      }
      return;
    }
    const track = this._videochat.getLocalCameraTrack();
    const live = track && track.readyState === 'live' ? track : null;
    const id = live ? live.id : null;
    if (id === this._trackId) return;
    this._trackId = id;
    this._setWebcamTrack(live);
  }

  /**
   * Swap the disc between the live mirrored feed and the shared standby
   * image. Standby FIRST, then dispose - never dispose a texture the current
   * material still samples.
   * @param {MediaStreamTrack|null} track
   */
  _setWebcamTrack(track) {
    if (this._disposed) return;
    const scene = this.scene;
    const oldTexture = this._webcamTexture;
    this._webcamTexture = null;
    if (!track) {
      this._swapDiscMaterial(
        createDiscMaterial(scene, 'localAvatarDiscMat', getStandbyTexture(scene), this._circleMask),
      );
      if (oldTexture) oldTexture.dispose();
      if (this._video) this._video.srcObject = null;
      return;
    }
    if (!this._video) {
      const video = document.createElement('video');
      video.id = 'local-avatar-video';
      video.autoplay = true;
      video.playsInline = true;
      video.muted = true;
      video.style.display = 'none';
      document.body.appendChild(video);
      this._video = video;
    }
    this._video.srcObject = new MediaStream([track]);
    this._video.play().catch(() => {
      /* autoplay is safe here (downstream of the camera-enable gesture) */
    });
    // The in-world live recipe (vScale -1), plus uScale -1 for the mirrored
    // selfie look - "see yourself as others see you", matching the arrival
    // preview. The published track others receive is NOT mirrored.
    const texture = new VideoTexture(
      'localAvatarWebcamTex',
      this._video,
      scene,
      true,
      true,
      undefined,
      {
        autoUpdateTexture: true,
      },
    );
    texture.vScale = -1;
    texture.uScale = -1;
    this._webcamTexture = texture;
    this._swapDiscMaterial(
      createDiscMaterial(scene, 'localAvatarWebcamMat', texture, this._circleMask),
    );
    if (oldTexture) oldTexture.dispose();
  }

  /**
   * Replace the disc material, disposing the old one (its textures are left
   * alone - the standby texture is shared per scene and the VideoTexture is
   * disposed explicitly by _setWebcamTrack/dispose).
   * @param {import('@babylonjs/core').StandardMaterial} mat
   */
  _swapDiscMaterial(mat) {
    const old = this._plane.material;
    this._plane.material = mat;
    if (old && old !== mat) old.dispose(false, false);
  }

  /**
   * Mirror the player camera's pose into the avatar root.
   * @param {import('@babylonjs/core').Vector3} position
   * @param {number} yaw
   */
  sync(position, yaw) {
    if (this._disposed) return;
    this.root.position.copyFrom(position);
    this.root.rotation.y = yaw;
  }

  /** @param {boolean} on */
  setEnabled(on) {
    if (this._disposed) return;
    this.root.setEnabled(!!on);
  }

  /** @returns {boolean} */
  isEnabled() {
    return !this._disposed && this.root.isEnabled();
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    UpdateManager.getInstance(this.scene).unregister(this._pollId);
    // Materials are disposed WITHOUT their textures - the standby texture is
    // the per-scene shared one from podAvatar.js. The circle mask and the
    // webcam VideoTexture are ours.
    this._plane?.material?.dispose(false, false);
    this._ring?.material?.dispose(false, false);
    this._webcamTexture?.dispose();
    this._circleMask?.dispose();
    if (this._video) {
      this._video.srcObject = null;
      this._video.remove();
      this._video = null;
    }
    this.root.dispose();
  }
}
